import { NUMBER_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '@/pages/Studies/utils/sharedColumnDefs';
import { buildEpitopeColDefs, tauEpitopeKeys } from '@/pages/Study/components/tauSamplesColDefs';
import { PROTEOFORM_SORT_FIELDS } from '@/types';
import type { ProteoformResultsTableConfig, SampleProteoformPublic } from '@/types';
import type { ColDef } from 'ag-grid-enterprise';

type TFunction = (key: string) => string;

// Without a floor, first-visit fit-to-width squeezes the fixed columns to make room for the epitope ones.
const FIXED_COLUMN_MIN_WIDTH = 120;

interface BuildRunSampleDetailsColDefsParams {
  t: TFunction;
  yes: string;
  no: string;
  /** Placeholder for a null value in any fixed column except Well, Sample and Proteoform Group. */
  empty: string;
  tableConfig?: ProteoformResultsTableConfig;
  sampleNameCellRenderer?: ColDef<SampleProteoformPublic>['cellRenderer'];
  /** Only super users receive `is_visible_to_customer`; for anyone else the column would be all blanks. */
  showCustomerVisibility?: boolean;
}

/** The backend's `sort_by` allowlist: the fixed `ProteoformSortField`s (one per fixed column) plus the
 *  assay panel's epitope keys. */
export function runSampleDetailsSortFields(tableConfig: ProteoformResultsTableConfig | undefined): string[] {
  return [...PROTEOFORM_SORT_FIELDS, ...tauEpitopeKeys(tableConfig)];
}

const textColumn = (
  field: keyof SampleProteoformPublic & string,
  headerName: string,
  empty?: string,
): ColDef<SampleProteoformPublic> => ({
  field,
  headerName,
  minWidth: FIXED_COLUMN_MIN_WIDTH,
  filter: 'agTextColumnFilter',
  filterParams: TEXT_FILTER_PARAMS,
  resizable: true,
  sortable: true,
  ...(empty === undefined
    ? {}
    : {
        valueFormatter: ({ value }) => (value === null || value === undefined || value === '' ? empty : String(value)),
      }),
});

const numberColumn = (
  field: keyof SampleProteoformPublic & string,
  headerName: string,
  empty: string,
): ColDef<SampleProteoformPublic> => ({
  field,
  headerName,
  minWidth: FIXED_COLUMN_MIN_WIDTH,
  filter: 'agNumberColumnFilter',
  filterParams: NUMBER_FILTER_PARAMS,
  resizable: true,
  sortable: true,
  valueFormatter: ({ value }: { value: number | null | undefined }) => value?.toLocaleString() ?? empty,
});

export function buildRunSampleDetailsColDefs({
  t,
  yes,
  no,
  empty,
  tableConfig,
  sampleNameCellRenderer,
  showCustomerVisibility = false,
}: BuildRunSampleDetailsColDefsParams): ColDef<SampleProteoformPublic>[] {
  const visibilityColumn: ColDef<SampleProteoformPublic> = {
    field: 'is_visible_to_customer',
    headerName: t('col_visible_to_customer'),
    minWidth: FIXED_COLUMN_MIN_WIDTH,
    resizable: true,
    // The run search's sort and filter contracts don't accept this field.
    sortable: false,
    filter: false,
    valueFormatter: ({ value }: { value: boolean | null | undefined }) => {
      if (value === null || value === undefined) return empty;
      return value ? yes : no;
    },
  };
  return [
    textColumn('flow_cell', t('col_flowcell'), empty),
    numberColumn('lane', t('col_lane'), empty),
    textColumn('well_position', t('col_well')),
    numberColumn('demux_index', t('col_demux_id'), empty),
    { ...textColumn('sample_name', t('col_sample')), cellRenderer: sampleNameCellRenderer },
    textColumn('sample_group', t('col_sample_groups'), empty),
    textColumn('proteoform_name', t('col_proteoform_group')),
    numberColumn('proteoform_abundance', t('col_abundance'), empty),
    ...(showCustomerVisibility ? [visibilityColumn] : []),
    ...buildEpitopeColDefs(tableConfig, yes, no),
  ];
}
